import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'

import { track } from '@/api/tracking'
import { ChromaKeyVideo } from '@/components/food-cost/ChromaKeyVideo'
import { FoodCostHeroCollaboration } from '@/components/food-cost/FoodCostHeroCollaboration'
import {
    ASSETS,
    CHROMA_KEY_SETTINGS,
    FOOD_COST_LANDING_PAGE,
    OUTBOUND_TRACK_OPTIONS,
} from '@/components/food-cost/constants'
import { TIPI_BUSINESS_COUNT } from '@/lib/landingStats/constants'
import type { LandingPage } from '@tipi/shared'

import BasqueLogo from '../../assets/images/basque-logo.svg?react'
import GOELogo from '../../assets/images/tipi-goe.svg?react'

const HERO_CTA_CLASS =
    'flex min-h-[clamp(2.375rem,6vh,3.5rem)] w-full max-w-[420px] items-center justify-center rounded-full bg-[#D13D1A] px-[clamp(0.625rem,3.2vw,1.5rem)] py-[clamp(0.4375rem,1vh,0.875rem)] text-center text-[length:clamp(0.6875rem,min(3.1vw,1.9vh),0.9375rem)] font-bold leading-[1.15] text-white [@media(max-height:740px)]:min-h-[2.375rem] [@media(max-height:740px)]:px-3 [@media(max-height:740px)]:py-2'

const HERO_SOURCE_1_HREF =
    'https://caixabanklab-campus.com/wp-content/uploads/sites/4/2025/03/ANUARIO-HOSTELERIA-2024.pdf'
const HERO_SOURCE_2_HREF = 'https://www.delectatech.com/resultados-del-canal-horeca-2025/'

const HERO_SOURCE_LINK_CLASS =
    'text-inherit no-underline underline-offset-2 transition-opacity hover:opacity-85 hover:underline'

type HeroCtaTarget = { signupUrl: string; onCtaClick?: never } | { onCtaClick: () => void; signupUrl?: never }

type FoodCostHeroProps = HeroCtaTarget & {
    title?: string
    subtitle?: string
    answerCapsule?: string
    ctaLabel?: string
    landingPage?: LandingPage
    mixpanelContext?: Record<string, unknown>
}

export const FoodCostHero = forwardRef<HTMLElement, FoodCostHeroProps>(function FoodCostHero(
    {
        onCtaClick,
        signupUrl,
        title,
        subtitle,
        answerCapsule,
        ctaLabel: ctaLabelProp,
        landingPage = FOOD_COST_LANDING_PAGE,
        mixpanelContext,
    },
    ref,
) {
    const { t } = useTranslation()

    const handleCtaClick = () => {
        track(
            'Hero CTA Clicked',
            {
                landing_page: landingPage,
                ...mixpanelContext,
                ...(signupUrl ? { cta: 'hero_cta', destination_url: signupUrl } : {}),
            },
            signupUrl ? OUTBOUND_TRACK_OPTIONS : undefined,
        )
        onCtaClick?.()
    }

    const ctaLabel = ctaLabelProp ?? t('foodCost.hero.cta', 'Quiero probarlo ya')

    return (
        <section
            ref={ref}
            data-foodcost-hero
            className='relative flex h-dvh max-h-[100dvh] flex-col overflow-hidden bg-[#17130E] bg-[radial-gradient(ellipse_120%_55%_at_50%_62%,rgba(244,80,42,0.22)_0%,rgba(244,80,42,0.05)_45%,rgba(23,19,14,0)_70%)] pt-[clamp(1rem,3vh,2.5rem)]'
        >
            <div className='mx-auto grid h-full min-h-0 w-full max-w-[1180px] flex-1 grid-cols-1 content-center gap-[clamp(0.75rem,2.2vh,2.5rem)] px-[22px] pb-[clamp(0.75rem,2vh,2rem)] md:grid-cols-[minmax(0,1.02fr)_minmax(280px,0.98fr)] md:items-center md:gap-8 md:px-10 md:pb-[clamp(1rem,2.5vh,3rem)]'>
                <div className='relative flex min-h-0 flex-col gap-[clamp(0.65rem,1.8vh,1.125rem)] md:max-w-[560px] md:self-center'>
                    <div className='inline-flex w-fit items-center gap-2 rounded-full border border-[#F5EEE4]/15 bg-[#F5EEE4]/5 px-[15px] py-[clamp(0.35rem,1vh,0.5rem)] text-[clamp(0.75rem,1.8vw,0.8125rem)] font-medium text-[#E8DFD2]'>
                        <span className='inline-block h-[7px] w-[7px] rounded-full bg-[#1FA855]' />
                        {t('foodCost.hero.clientBadge', '{{count}} negocios han mejorado con Tipi', {
                            count: TIPI_BUSINESS_COUNT,
                        })}
                    </div>
                    <h1
                        className={`m-0 text-[length:clamp(1.875rem,min(11.5vw,7.25vh),4.75rem)] font-medium leading-[0.98] tracking-[clamp(-0.12rem,-0.04em,-0.1875rem)] text-[#F5EEE4] ${title ? 'max-w-[18ch]' : 'max-w-[14ch]'}`}
                    >
                        {title ?? t('foodCost.hero.title', 'Escandallos por voz en menos de un minuto')}
                    </h1>
                    <h2 className='m-0 max-w-[28ch] text-[length:clamp(0.9375rem,2.4vw,1.25rem)] font-normal leading-[1.5] text-[#F4502A]'>
                        {subtitle ?? t('foodCost.hero.subtitle', '¿Sabes cuánto te cuestan tus platos?')}
                    </h2>
                    <p className='m-0 max-w-[36ch] text-[length:clamp(0.8125rem,2vw,0.9375rem)] leading-snug text-[#C9C0B3] md:text-[15px] md:leading-[1.65]'>
                        {answerCapsule ?? t('foodCost.hero.answerCapsule')}
                    </p>
                </div>

                <div className='relative flex min-h-0 w-full flex-col items-center justify-center gap-[clamp(0.5rem,1.5vh,1rem)] md:max-w-[600px] md:justify-self-center'>
                    <div className='relative w-full max-w-[min(100%,clamp(15rem,42vw,22.5rem))] [@media(max-height:740px)]:max-w-[min(100%,17.5rem)]'>
                        <div className='pointer-events-none absolute left-1/2 top-1/2 h-[82%] w-[84%] -translate-x-1/2 -translate-y-1/2 rounded-[999px] bg-[radial-gradient(circle,rgba(244,80,42,0.16)_0%,rgba(244,80,42,0.08)_42%,rgba(23,19,14,0.02)_72%,rgba(23,19,14,0)_100%)] blur-[34px]' />
                        <div className='relative overflow-visible'>
                            <ChromaKeyVideo
                                src={ASSETS.heroVideoSource}
                                poster={ASSETS.heroVideoPoster}
                                settings={CHROMA_KEY_SETTINGS}
                                deferUntilIdle
                                aria-label={t(
                                    'foodCost.hero.characterAnimationAriaLabel',
                                    'Animación del personaje Tipi',
                                )}
                                className='relative z-0 mx-auto w-full scale-[clamp(0.88,0.95,1.1)] [@media(max-height:740px)]:scale-[0.88]'
                            />
                        </div>
                    </div>

                    <p className='m-0 max-w-[28ch] text-center text-[clamp(0.75rem,1.8vw,0.9375rem)] leading-[1.35] text-[#B8AFA3]'>
                        <span className='block italic'>
                            {t('foodCost.hero.tagline.line1', 'El fuego te ayuda a cocinar.')}
                        </span>
                        <span className='mt-1 block text-[#F5EEE4]'>
                            {t('foodCost.hero.tagline.line2', 'Ahora, también a ganar dinero.')}
                        </span>
                    </p>

                    <div className='flex w-full flex-col items-center gap-1.5'>
                        {signupUrl ? (
                            <a
                                href={signupUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                onClick={handleCtaClick}
                                className={`${HERO_CTA_CLASS} no-underline`}
                            >
                                {ctaLabel}
                            </a>
                        ) : (
                            <button type='button' onClick={handleCtaClick} className={HERO_CTA_CLASS}>
                                {ctaLabel}
                            </button>
                        )}
                        {signupUrl && (
                            <p className='m-0 text-center text-[clamp(0.6875rem,1.6vw,0.8125rem)] leading-snug text-[#B8AFA3]'>
                                {t('foodCost.hero.ctaTrust', 'Cancela antes de 30 días y no pagas')}
                            </p>
                        )}
                    </div>

                    <div className='relative z-10 flex w-full flex-col items-center gap-2 text-center'>
                        <FoodCostHeroCollaboration />
                        <p className='m-0 text-[clamp(0.75rem,1.8vw,0.9375rem)] italic text-[#B8AFA3]'>
                            {t('foodCost.hero.awardsLabel', 'Reconocida y premiada por')}
                        </p>
                        <div
                            role='group'
                            className='flex items-center justify-center gap-4'
                            aria-label={t('foodCost.hero.awardsAriaLabel', 'GOe y Basque Culinary Center')}
                        >
                            <GOELogo
                                className='h-[clamp(2rem,5vh,3.5rem)] w-auto [&>g>path]:fill-[#E8DFD2]'
                                aria-hidden
                            />
                            <BasqueLogo
                                className='h-[clamp(2rem,5vh,3.5rem)] w-auto [&>g>path]:fill-[#E8DFD2]'
                                aria-hidden
                            />
                        </div>
                        <aside
                            aria-label={t('foodCost.hero.sourcesLabel', 'Fuentes')}
                            className='mt-1 w-full max-w-[36ch]'
                        >
                            <ul className='m-0 flex list-none flex-col items-center gap-0.5 p-0 text-[10px] leading-snug text-[#B8AFA3]'>
                                <li className='m-0'>
                                    <a
                                        href={HERO_SOURCE_1_HREF}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={HERO_SOURCE_LINK_CLASS}
                                    >
                                        {t('foodCost.hero.source1')}
                                    </a>
                                </li>
                                <li className='m-0'>
                                    <a
                                        href={HERO_SOURCE_2_HREF}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={HERO_SOURCE_LINK_CLASS}
                                    >
                                        {t('foodCost.hero.source2')}
                                    </a>
                                </li>
                            </ul>
                        </aside>
                    </div>
                </div>
            </div>
        </section>
    )
})
